import React, { useState } from "react";
import { Box, Button, Chip, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DynamicTableForJson from "../../../../../data/DynamicTable/dynamicTableForJson";
import { AssetFinanceUrl } from "../../../../../config";
import CollateralAssetForm from "./form";
import ViewCollateralDocumentsModal from "./ViewDocumentsModal";

const base_url = AssetFinanceUrl.uri;

const STATUS_COLORS = {
  DRAFT: "default",
  PENDING_VERIF: "warning",
  PENDING_REVIEW: "warning",
  PENDING_VALUATION: "warning",
  APPROVED: "success",
  ACTIVE: "success",
  REJECTED: "error",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const collateralAssetTourSteps = [
  {
    target: ".dynamic-table-header",
    content:
      "Register and manage this customer's financed collateral assets and their supporting documents here.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-add-button",
    content:
      "Register a new collateral asset — vehicle, land, or other movable asset.",
    placement: "right",
  },
  {
    target: ".dynamic-table-actions",
    content:
      "Edit an asset, review its documents, or manage co-owner/director details from this menu.",
    placement: "left",
  },
];

const columns = [
  { field: "Id", headerName: "Asset ID", width: 90 },
  { field: "AssetTypeName", headerName: "Asset Type", flex: 1 },
  { field: "SubTypeName", headerName: "Sub Type", flex: 1 },
  {
    field: "IndicativeValue",
    headerName: "Indicative Value",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.IndicativeValue),
  },
  {
    field: "LtvPct",
    headerName: "LTV (%)",
    flex: 0.8,
    valueGetter: (params) => {
      let metadata = {};
      try {
        if (params.row.Metadata) {
          metadata =
            typeof params.row.Metadata === "string"
              ? JSON.parse(params.row.Metadata)
              : params.row.Metadata;
        }
      } catch {}
      const val = params.row.LtvPct ?? metadata.ltvPct;
      return val != null && val !== "" ? `${val}%` : "-";
    },
  },
  {
    field: "Status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => {
      const status = params.row.AssetStatus || "DRAFT";
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={String(status).replace(/_/g, " ")}
            size="small"
            color={STATUS_COLORS[status] || "default"}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
          {status === "REJECTED" && params.row.RejectionReason && (
            <Tooltip title={params.row.RejectionReason}>
              <WarningAmberOutlinedIcon fontSize="small" color="error" />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  { field: "OwnershipType", headerName: "OwnershipType", flex: 1 },
  { field: "CreatedAt", headerName: "Date Added", flex: 1 },
];

const CollateralAssetTable = () => {
  const navigate = useNavigate();
  const customerId = localStorage.getItem("CustomerId");
  const customerstatus = parseInt(localStorage.getItem("Status") || 0);
  const showButton =
    customerstatus === 1 ||
    customerstatus === 0 ||
    localStorage.getItem("Status") === null;

  const [viewDocsOpen, setViewDocsOpen] = useState(false);
  const [selectedAssetForDocs, setSelectedAssetForDocs] = useState(null);

  const handleOpenDocsModal = (row) => {
    setSelectedAssetForDocs(row);
    setViewDocsOpen(true);
  };

  const actions = {
    entity: "Collateral Assets",
    isSubright: true,
    showStatus: true,
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: showButton,
      permission: "EditPermission",
    },
    ViewDocuments: {
      key: "VIEWDOCUMENTS",
      button_name: "View Documents",
      Show_Button: true,
      permission: "EditPermission",
      onClick: (row) => handleOpenDocsModal(row),
    },
    // Only rendered when the row is actually co-owned. If DynamicTableForJson
    // doesn't yet call Show_Button as a function per row, this needs a small
    // patch there — see note above.
    ManageCoOwner: {
      key: "MANAGECOOWNER",
      button_name: "Manage Co-Owner",
      Show_Button: (row) => (row?.OwnershipType || "SINGLE") === "CO_OWNED",
      permission: "EditPermission",
      // onClick handled inside DynamicTableForJson's own switch statement,
      // which already does: localStorage.setItem("collateralAssetId", Id);
      // localStorage.setItem("ownershiptype", row.OwnershipType); navigate("/CoOwners");
    },
    // Only rendered when the row is actually company-owned.
    ManageDirectors: {
      key: "MANAGEDIRECTORS",
      button_name: "Manage Directors",
      Show_Button: (row) =>
        (row?.OwnershipType || "SINGLE") === "COMPANY_OWNED",
      permission: "EditPermission",
      // onClick handled inside DynamicTableForJson's own switch statement,
      // which already does: localStorage.setItem("collateralAssetId", Id);
      // localStorage.setItem("ownershiptype", row.OwnershipType); navigate("/directors");
    },
    add: {
      key: "ADD",
      button_name: "Add Asset",
      Show_Button: showButton,
      permission: "CreatePermission",
    },
    AddValuation: {
      key: "ADDVALUATION",
      button_name: "Add Valuation",
      Show_Button: showButton,
      permission: "CreatePermission",
    },
    dataObject: {
      ignoreFields: [
        "CreatedBy",
        "UpdatedBy",
        "UpdatedAt",
        "Metadata",
        "CompanyId",
        "CustomerId",
        "CurrentValuationId",
      ],
      objectName: "collateralAssetByColumnValues",
      parameters: { customerId: parseInt(customerId) },
      inputObjectName: "asset",
    },
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button onClick={() => navigate(-1)} variant="contained">
          Back to Customers
        </Button>
      </Box>
      <DynamicTableForJson
        title="Collateral Assets"
        subtitle="Register and manage this customer's financed assets and their supporting documents"
        FormComponent={CollateralAssetForm}
        columns={columns}
        base_url={base_url}
        actions={actions}
        tourSteps={collateralAssetTourSteps}
      />
      <ViewCollateralDocumentsModal
        open={viewDocsOpen}
        onClose={() => setViewDocsOpen(false)}
        asset={selectedAssetForDocs}
      />
    </Box>
  );
};

export default CollateralAssetTable;
